import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/stores/auth';
import { ApiError } from '@/lib/api';

export function LoginPage({ onSuccess }: { onSuccess: () => void }) {
  const login = useAuth((s) => s.login);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await login(email, password);
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError('Этот аккаунт без admin-доступа');
      } else if (err instanceof ApiError && err.status === 401) {
        setError('Неверный email или пароль');
      } else {
        setError('Не удалось войти');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden"
      style={{
        background:
          'radial-gradient(ellipse at top, rgba(255, 92, 53, 0.08), transparent 50%), radial-gradient(ellipse at bottom, rgba(168, 85, 247, 0.04), transparent 50%), #09090b',
      }}
    >
      {/* Decorative top gradient line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#ff5c35]/40 to-transparent" />

      <div className="w-full max-w-sm space-y-6 relative">
        {/* Brand */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center gap-2 text-[15px] font-medium tracking-tight">
            <span className="inline-block h-2 w-2 rounded-full bg-[#ff5c35] shadow-[0_0_12px_rgba(255,92,53,0.8)]" />
            <span className="text-zinc-100">Huddle</span>
            <span className="text-zinc-500">admin</span>
          </div>
          <div className="text-[11px] text-zinc-600 uppercase tracking-[0.18em]">
            Управление платформой
          </div>
        </div>

        {/* Form card */}
        <form
          onSubmit={submit}
          className="space-y-4 rounded-xl bg-zinc-900/40 backdrop-blur-xl p-6 border border-zinc-800/40 shadow-[0_12px_32px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.04)]"
        >
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Пароль</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          {error && (
            <div className="rounded-md border border-rose-900/50 bg-rose-950/40 px-3 py-2 text-xs text-rose-200">
              {error}
            </div>
          )}
          <Button type="submit" disabled={busy} className="w-full">
            {busy ? 'Вход…' : 'Войти'}
          </Button>
        </form>

        <div className="text-center text-[11px] text-zinc-600">
          Только для администраторов
        </div>
      </div>
    </div>
  );
}
